import { ApplicationTypes, Model, attachComponent } from 'awr-domain'
import { Sphere, Vector3 } from 'three'

import {
  boundingSphereComponent,
  customPriceFooterComponent,
  customSummaryPageComponent,
  memoEval,
  propertyPageComponent,
} from '@salsita-ai/platform-project-sdk/domain'
import {
  AttributeListSelector,
  type AttributeListSelectorProps,
  AttributeSwitchSelector,
  AttributeToggleSelector,
  DomainControlsAnalytics,
  GenericDimensionInput,
  MaterialSwatchSelector,
  useAttributeSelection,
  useResolveLocalizable,
} from '@salsita-ai/platform-project-sdk/react'
import { ListSelector, NumericalInput, PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import { AppPrice } from '../ui/AppPrice.js'
import { AppSummary } from '../ui/AppSummary.js'

const STAINLESS_HANDRAIL_DISABLED_MESSAGE = 'Round stainless and square stainless handrail options coming soon'

const handrailTypeSelectedEvent = DomainControlsAnalytics.AttributeSelected('ListItem')

const HandrailTypeSelector = ({ entityId }: Pick<AttributeListSelectorProps, 'entityId'>) => {
  const { options, value } = useAttributeSelection(entityId, 'handrailType', undefined, handrailTypeSelectedEvent)
  const resolveLocalizable = useResolveLocalizable()

  return (
    <ListSelector.Root value={value} direction="vertical">
      {options.map(option => {
        const temporarilyDisabled =
          option.value === Model.HandrailType.RoundStainless || option.value === Model.HandrailType.SquareStainless

        return (
          <ListSelector.Item
            key={String(option.value)}
            title={resolveLocalizable(option.title)}
            description={resolveLocalizable(option.description)}
            onChange={option.onSelect}
            onDebugClick={option.trace}
            value={option.value}
            checked={value === option.value}
            disabled={option.error || temporarilyDisabled}
            disabledMessage={temporarilyDisabled ? STAINLESS_HANDRAIL_DISABLED_MESSAGE : option.hint}
          />
        )
      })}
    </ListSelector.Root>
  )
}

export const deckComponents = [
  attachComponent
    .withEntity('Deck')
    .withComponent(boundingSphereComponent, 'main')
    .attach(
      memoEval(
        s => [
          s.$boundingBox.min.x,
          s.$boundingBox.min.y,
          s.$boundingBox.min.z,
          s.$boundingBox.max.x,
          s.$boundingBox.max.y,
          s.$boundingBox.max.z,
        ],
        (minX, minY, minZ, maxX, maxY, maxZ) => {
          const center = new Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2)
          const radius = Math.max(maxX - minX, maxY - minY, maxZ - minZ) / 2

          const shape = new Sphere(center, radius)

          return {
            shape,
          }
        }
      )
    ),

  attachComponent
    .withEntity('Deck')
    .withComponent(propertyPageComponent, 'timberAttributes')
    .withGradualFilter({
      navigationStates: [ApplicationTypes.NavigationState.Properties],
    })
    .attach(
      memoEval(
        s => [s.id, s.postType, s.useThinIntermediatePosts],
        (id, postType, useThinIntermediatePosts) => {
          if (postType !== Model.PostType.Timber) {
            return {
              page: null,
            }
          }

          return {
            page: (
              <>
                <PropertyContainer.Item
                  title="Post Style"
                  description="Select the style of post for your balustrade. All AWR posts 960mm high with 80mm wire spacing."
                >
                  <AttributeToggleSelector entityId={id} instanceName="postType" />
                </PropertyContainer.Item>
                <ListSelector.Root value="own" direction="vertical">
                  <ListSelector.Item title="I will supply my own timber posts" value="own" checked={true} />
                </ListSelector.Root>

                <PropertyContainer.Item
                  title="Wire Balustrade System"
                  description="Choose your wire balustrade system for timber posts"
                >
                  <AttributeListSelector entityId={id} instanceName="timberFixingSystem" />
                </PropertyContainer.Item>
                <PropertyContainer.Item
                  title="Wire Balustrade Finish"
                  description="Choose bwteen Bright Finish and BlackEtch Finishes"
                >
                  <MaterialSwatchSelector entityId={id} instanceName="finish" />
                </PropertyContainer.Item>
                <PropertyContainer.Item
                  title="Number of wires"
                  description="Select how many wires you want in your balustrade"
                >
                  <GenericDimensionInput entityId={id} instanceName="wires" />
                </PropertyContainer.Item>
                <PropertyContainer.Item>
                  <AttributeSwitchSelector
                    entityId={id}
                    instanceName="useThinIntermediatePosts"
                    title="Use thin intermediate posts"
                    description="50x10 instead of the standard 50x50"
                  />
                </PropertyContainer.Item>
                {useThinIntermediatePosts && (
                  <PropertyContainer.Item title="Intermediate post type" description="Description">
                    <MaterialSwatchSelector entityId={id} instanceName="thinIntermediatePostType" />
                  </PropertyContainer.Item>
                )}
              </>
            ),
          }
        }
      )
    ),

  attachComponent
    .withEntity('Deck')
    .withComponent(propertyPageComponent, 'metalAttributes')
    .withGradualFilter({
      navigationStates: [ApplicationTypes.NavigationState.Properties],
    })
    .attach(
      memoEval(
        s => [s.id, s.postType, s.useThinIntermediatePosts, s.$isStainless, s.postStyle],
        (id, postType, useThinIntermediatePosts, isStainless, postStyle) => {
          if (postType !== Model.PostType.Metal) {
            return {
              page: null,
            }
          }

          return {
            page: (
              <>
                <PropertyContainer.Item
                  title="Post Style"
                  description="Select the style of post for your balustrade. All AWR posts 960mm high with 80mm wire spacing."
                >
                  <AttributeToggleSelector entityId={id} instanceName="postType" />
                </PropertyContainer.Item>
                <AttributeListSelector entityId={id} instanceName="postStyle" />

                {isStainless && (
                  <PropertyContainer.Item
                    title="Post finish"
                    description="Select your finish. Choose between satin finish and mirror polish"
                  >
                    <MaterialSwatchSelector entityId={id} instanceName="postStainlessFinish" />
                  </PropertyContainer.Item>
                )}
                <PropertyContainer.Item
                  title="Handrail Type"
                  description="Select your handrail type (round, square, or timber)."
                >
                  <HandrailTypeSelector entityId={id} />
                </PropertyContainer.Item>
                <PropertyContainer.Item>
                  <AttributeSwitchSelector
                    entityId={id}
                    instanceName="useThinIntermediatePosts"
                    title="Use thin intermediate posts"
                    description="50x10 instead of the standard 50x50"
                  />
                </PropertyContainer.Item>
                {useThinIntermediatePosts && postStyle === Model.MetalPostStyleType.Custom && (
                  <PropertyContainer.Item title="Intermediate post type" description="Description">
                    <MaterialSwatchSelector entityId={id} instanceName="thinIntermediatePostType" />
                  </PropertyContainer.Item>
                )}
                <PropertyContainer.Item
                  title="Wire Balustrade System"
                  description="Choose your wire balustrade system for metal posts"
                >
                  <AttributeListSelector entityId={id} instanceName="metalFixingSystem" />
                </PropertyContainer.Item>
                <PropertyContainer.Item
                  title="Wire Balustrade Finish"
                  description="Choose bwteen Bright Finish and BlackEtch Finishes"
                >
                  <MaterialSwatchSelector entityId={id} instanceName="finish" />
                </PropertyContainer.Item>
                {postStyle === Model.MetalPostStyleType.Custom && (
                  <PropertyContainer.Item
                    title="Number of wires"
                    description="Select how many wires you want in your balustrade"
                  >
                    <GenericDimensionInput entityId={id} instanceName="wires" />
                  </PropertyContainer.Item>
                )}
                {postStyle !== Model.MetalPostStyleType.Custom && (
                  <PropertyContainer.Item title="Number of wires" description="All posts suit 11 wires at 80mm spacing">
                    <NumericalInput
                      inputFieldProps={{
                        invalidNumberErrorMessage: 'Please enter a valid number',
                      }}
                      unit="wires"
                      disabled
                      value={11}
                    />
                  </PropertyContainer.Item>
                )}
              </>
            ),
          }
        }
      )
    ),

  attachComponent
    .withEntity('Deck')
    .withComponent(propertyPageComponent, 'summary')
    .withGradualFilter({
      navigationStates: [ApplicationTypes.NavigationState.Summary],
    })
    .attach(
      memoEval(
        s => [s.$summary],
        summary => ({
          page: <AppSummary summary={summary} />,
        })
      )
    ),
  attachComponent
    .withEntity('Deck')
    .withComponent(customSummaryPageComponent, 'customSummary')
    .withGradualFilter({
      modes: ['read'],
    })
    .attach(
      memoEval(
        s => [s.$summary],
        summary => ({
          summaryNavigationState: ApplicationTypes.NavigationState.Build,
          page: <AppSummary summary={summary} />,
        })
      )
    ),

  attachComponent
    .withEntity('Deck')
    .withComponent(customPriceFooterComponent, 'default')
    .attach(
      memoEval(
        s => [s.$price],
        price => ({
          node: <AppPrice price={price} />,
        })
      )
    ),
]
